<template>
  <v-container app>
    <!-- Estado de carga -->
    <v-row v-if="isLoading" justify="center" class="py-10">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-row>

    <!-- Sin boletos -->
    <v-row v-else-if="!purchasedEvents.length" justify="center" class="py-10">
      <v-col cols="12" class="text-center">
        <v-icon size="72" color="grey lighten-2">mdi-ticket-outline</v-icon>
        <p class="mt-4 subtitle-1 grey--text text--lighten-1">
          Aún no tienes boletos comprados
        </p>
      </v-col>
    </v-row>

    <!-- Lista de boletos -->
    <v-row v-else justify="start" class="mx-0 my-2">
      <v-col
        v-for="item in purchasedEvents"
        :key="item._id"
        cols="12"
        sm="6"
        md="4"
      >
        <v-card class="mx-auto" rounded="xl" elevation="0" outlined>
          <v-img
            :src="item.event.banner || require('@/assets/images/shared/withoutbanner.png')"
            height="150"
          ></v-img>

          <v-card-title
            class="font-weight-bold pt-2 pb-0"
            style="word-break: break-word; font-size: 1rem;"
          >
            {{ item.event.name }}
          </v-card-title>

          <v-card-subtitle class="pb-1 pt-1">
            <div v-if="item.event.dateConfiguration">
              <v-icon x-small class="mr-1">mdi-calendar</v-icon>
              {{ formatDate(item.event.dateConfiguration.startDate) }}
            </div>
            <div v-if="item.ticket" class="mt-1">
              <v-chip x-small color="primary" outlined>
                {{ item.ticket.name }}
              </v-chip>
              <v-chip
                v-if="item.ticket.modality"
                x-small
                class="ml-1"
                :color="modalityColor(item.ticket.modality)"
                text-color="white"
              >
                {{ modalityLabel(item.ticket.modality) }}
              </v-chip>
            </div>
          </v-card-subtitle>

          <v-card-actions class="pt-0">
            <v-btn
              v-if="isPresential(item)"
              color="secondary"
              rounded
              small
              depressed
              @click="openQr(item)"
            >
              <v-icon left small>mdi-qrcode</v-icon>
              Ver QR
            </v-btn>
            <v-btn
              v-if="hasZoom(item)"
              color="primary"
              rounded
              small
              depressed
              :loading="joiningZoom === item._id"
              @click="joinZoom(item)"
            >
              <v-icon left small>mdi-video</v-icon>
              Unirse a Zoom
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog del QR (presencial) -->
    <v-dialog v-model="qrDialog" max-width="420" scrollable>
      <v-card v-if="activeQrItem" rounded="xl">
        <v-card-title class="text-h6 font-weight-bold pb-2">
          {{ activeQrItem.event.name }}
        </v-card-title>
        <v-card-subtitle v-if="activeQrItem.event.dateConfiguration" class="pt-0 pb-2">
          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
          {{ formatDate(activeQrItem.event.dateConfiguration.startDate) }}
        </v-card-subtitle>
        <v-card-text class="text-center">
          <v-img
            :src="qrUrl"
            :max-width="300"
            max-height="300"
            class="mx-auto"
            contain
            :aspect-ratio="1"
          >
            <template #placeholder>
              <v-row class="fill-height ma-0" justify="center" align="center">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
          <p class="mt-4 mb-1 font-weight-medium">
            Tu código de acceso
          </p>
          <p class="caption grey--text">
            Presenta este código en la entrada del evento.
          </p>
          <p
            v-if="getLocation(activeQrItem)"
            class="caption grey--text mt-3"
          >
            <v-icon x-small>mdi-map-marker</v-icon>
            {{ getLocation(activeQrItem) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeQr">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

export default {
  name: 'MyPurchasedTickets',
  data() {
    return {
      isLoading: false,
      joiningZoom: null,
      qrDialog: false,
      activeQrItem: null,
    };
  },
  computed: {
    ...mapState('memberEvents', ['purchasedEvents']),
    qrUrl() {
      if (!this.activeQrItem) return '';
      return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${this.activeQrItem._id}`;
    },
  },
  methods: {
    ...mapMutations('loader', ['loading', 'loaded']),
    ...mapMutations('notification', ['show']),
    ...mapActions('memberEvents', ['fetchMyPurchasedEvents', 'fetchZoomJoinLink']),

    hasZoom(item) {
      if (
        !item.event ||
        !item.event.zoomConfiguration ||
        !item.event.zoomConfiguration.meetingId
      ) {
        return false;
      }
      const modality = item.event.eventModality;
      return modality === 'VIRTUAL' || modality === 'HYBRID';
    },

    isPresential(item) {
      return item.ticket && item.ticket.modality === 'PRESENTIAL';
    },

    modalityLabel(modality) {
      const labels = {
        PRESENTIAL: 'Presencial',
        VIRTUAL: 'Virtual',
        HYBRID: 'Híbrido',
      };
      return labels[modality] || modality;
    },

    modalityColor(modality) {
      const colors = {
        PRESENTIAL: 'green',
        VIRTUAL: 'blue',
        HYBRID: 'purple',
      };
      return colors[modality] || 'grey';
    },

    getLocation(item) {
      return (
        item.event &&
        item.event.presentialEventConfiguration &&
        item.event.presentialEventConfiguration.formattedAddress
      );
    },

    openQr(item) {
      this.activeQrItem = item;
      this.qrDialog = true;
    },

    closeQr() {
      this.qrDialog = false;
      this.activeQrItem = null;
    },

    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.substring(0, 10).split('-');
      return `${day}-${month}-${year}`;
    },

    async joinZoom(item) {
      this.joiningZoom = item._id;
      try {
        const result = await this.fetchZoomJoinLink(item.event._id);
        if (result.status) {
          window.open(result.joinUrl, '_blank', 'noopener,noreferrer');
        } else {
          this.show({
            text: result.message || 'No se pudo obtener el enlace de Zoom',
            color: 'error',
          });
        }
      } finally {
        this.joiningZoom = null;
      }
    },
  },
  async created() {
    this.isLoading = true;
    try {
      await this.fetchMyPurchasedEvents();
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  },
};
</script>
